import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2 } from "lucide-react";
import type { CartItem as CartItemType } from "@/lib/shopify";

interface CartItemProps {
  item: CartItemType;
  onUpdateQuantity: (variantId: string, quantity: number) => void;
  onRemove: (variantId: string) => void;
}

export const CartItem = ({ item, onUpdateQuantity, onRemove }: CartItemProps) => {
  const handleDecrement = () => {
    onUpdateQuantity(item.variantId, item.quantity - 1);
  };

  const handleIncrement = () => {
    onUpdateQuantity(item.variantId, item.quantity + 1);
  };

  const handleRemove = () => {
    onRemove(item.variantId);
  };

  return (
    <div className="flex gap-4 p-2" role="listitem">
      <div className="w-16 h-16 bg-secondary/20 rounded-md overflow-hidden flex-shrink-0">
        {item.product.node.images?.edges?.[0]?.node && (
          <img
            src={item.product.node.images.edges[0].node.url}
            alt={item.product.node.title}
            className="w-full h-full object-cover"
          />
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <h4 className="font-medium truncate">{item.product.node.title}</h4>
        <p className="text-sm text-muted-foreground">
          {item.selectedOptions.map(option => option.value).join(' • ')}
        </p>
        <p className="font-semibold" aria-label={`Price: ${item.price.currencyCode} ${parseFloat(item.price.amount).toFixed(2)}`}>
          {item.price.currencyCode} ${parseFloat(item.price.amount).toFixed(2)}
        </p>
      </div>
      
      <div className="flex flex-col items-end gap-2 flex-shrink-0">
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={handleRemove}
          aria-label={`Remove ${item.product.node.title} from cart`}
        >
          <Trash2 className="h-3 w-3" aria-hidden="true" />
        </Button>
        
        <div className="flex items-center gap-1" role="group" aria-label="Quantity controls">
          <Button
            variant="outline"
            size="icon"
            className="h-6 w-6"
            onClick={handleDecrement}
            aria-label="Decrease quantity"
          >
            <Minus className="h-3 w-3" aria-hidden="true" />
          </Button>
          <span className="w-8 text-center text-sm" aria-label={`Quantity: ${item.quantity}`}>
            {item.quantity}
          </span>
          <Button
            variant="outline"
            size="icon"
            className="h-6 w-6"
            onClick={handleIncrement}
            aria-label="Increase quantity"
          >
            <Plus className="h-3 w-3" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </div>
  );
};
